import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error monitoring and performance tracking
 * Configured for Admin Dashboard application
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error monitoring disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      // Browser tracing without routing instrumentation (not available in current version)
      Sentry.browserTracingIntegration(),
    ],
    // Set sample rate for performance monitoring
    // Use lower rate in production to reduce quota usage
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Debug mode in development
    debug: environment === 'development',
    // Release tracking (optional, set via build)
    release: import.meta.env.VITE_APP_VERSION || '0.0.0',
    // Attached metadata
    initialScope: {
      tags: {
        app: 'litla-admin',
      },
    },
  });
}

/**
 * Capture exception and send to Sentry
 */
export function captureException(error: unknown): string {
  const eventId = Sentry.captureException(error);
  return eventId || 'unknown';
}

/**
 * Capture message and send to Sentry
 */
export function captureMessage(message: string, level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'): string {
  const eventId = Sentry.captureMessage(message, level);
  return eventId || 'unknown';
}

/**
 * Set user context for error tracking
 */
export function setUser(userId: string | null, email?: string, username?: string): void {
  if (userId) {
    Sentry.setUser({
      id: userId,
      email,
      username,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Set additional context for error tracking
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  Sentry.setContext(name, context);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string = 'default',
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}
